/** @jsxImportSource theme-ui */
import React from "react";
import { jsx, Button, Box, Flex } from "theme-ui";
import PropTypes from "prop-types";
import { NavLink, Chip } from "../atoms";
import { colors } from "../../styles/theme";
import { useResponsiveValue } from "../../../src/utils/media-query";

const baseContainerStyle = {
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  maxWidth: "43rem",
  margin: "0 auto",
};

const styles = {
  webContainer: {
    ...baseContainerStyle,
  },
  mobileContainer: {
    ...baseContainerStyle,
    flexDirection: "column",
  },
  buttonBox: {
    margin: "0 4.5rem",
    display: "flex",
    flexDirection: "row",
  },
  dottedSpan: {
    margin: "0 1rem",
    marginTop: "0.75rem",
    color: colors.neutral800,
  },
  button: {
    margin: "0 0.5rem",
    padding: "0 0.5rem",
    background: "transparent",
  },
  mobileStyle: {
    alignItems: "center",
    minWidth: "15rem",
    marginTop: "1rem",
  },
};

const Pagination = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  version,
  customStyles
}) => {

  if (Math.ceil(totalItems / itemsPerPage) <= 1) {
    return null;
  }
  
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const showPrevious = currentPage > 1;
  const showNext = currentPage < totalPages;

  const calculateVisiblePages = () => {
    const pageRange = (start, end) => {
      const result = [];
      for (let i = start; i <= end; i++) {
        result.push(i);
      }
      return result;
    };
    if (totalPages <= 5) {
      return pageRange(1, totalPages);
    }
    if (currentPage <= 3) {
      return [...pageRange(1, 3), "...", totalPages];
    }
    if (currentPage >= totalPages - 2) {
      return [1, "...", ...pageRange(totalPages - 2, totalPages)];
    }
    return [1, '...', ...pageRange(currentPage - 1, currentPage + 1), '...', totalPages];
  };

  const handlePageChange = (newPage) => {
    onPageChange(newPage);
  };

  const PageLink = () => (
		<Box sx={styles.buttonBox}>
			{calculateVisiblePages().map((page, index) =>
				page === "..." ? (
					<span sx={styles.dottedSpan} key={`dots-${index}`}>
						...
					</span>
				) : (
					<Button sx={styles.button} key={`page-${page}`}>
						{page === currentPage ? (
							<Chip label={page} type="subtle" />
						) : (
							<NavLink
								to="#"
								onClick={() => handlePageChange(page)}
								customStyles={{ color: colors.neutral800 }}
							>
								{page}
							</NavLink>
						)}
					</Button>
				)
			)}
		</Box>
	);

  const PaginationMobile = () => (
    <Flex sx={{ ...styles.mobileContainer, ...customStyles }}>
      <PageLink />
      <Flex
        sx={{
          ...styles.mobileStyle,
          justifyContent: showPrevious && showNext ? "space-between" : "center",
        }}
      >
        {showPrevious && (
          <NavLink
            to="#"
            iconName="ArrowLeft"
            iconPosition="left"
            onClick={() => handlePageChange(currentPage - 1)}
            customStyles={{color: colors.neutral800}}
          >
            Previous
          </NavLink>
        )}
        {showNext && (
          <NavLink
            to="#"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={() => handlePageChange(currentPage + 1)}
            customStyles={{color: colors.neutral800}}
          >
            Next
          </NavLink>
        )}
      </Flex>
    </Flex>
  );

  const PaginationWeb = () => (
    <Flex sx={{ ...styles.webContainer, ...customStyles }}>
      {showPrevious && (
        <NavLink
          to="#"
          iconName="ArrowLeft"
          iconPosition="left"
          onClick={() => handlePageChange(currentPage - 1)}
          customStyles={{color: colors.neutral800}}
        >
          Previous
        </NavLink>
      )}
      <PageLink />
      {showNext && (
        <NavLink
          to="#"
          iconName="ArrowRight"
          iconPosition="right"
          onClick={() => handlePageChange(currentPage + 1)}
          customStyles={{color: colors.neutral800}}
        >
          Next
        </NavLink>
      )}
    </Flex>
  );

  const paginationComponent = useResponsiveValue([
    <PaginationMobile />,
    <PaginationMobile />,
    <PaginationWeb />,
    <PaginationWeb />,
  ]);

  const isStorybook = process.env.STORYBOOK === "true";

  if (isStorybook && version === "web") return <PaginationWeb />;
  if (isStorybook && version === "mobile") return <PaginationMobile />;

  return paginationComponent;
};

Pagination.defaultProps = {
  currentPage: 1,
  totalItems: 0,
  itemsPerPage: 10,
  onPageChange: () => {},
  customStyles: {},
};

Pagination.propTypes = {
  currentPage: PropTypes.number,
  totalItems: PropTypes.number,
  itemsPerPage: PropTypes.number,
  onPageChange: PropTypes.func,
  customStyles: PropTypes.object,
  version: PropTypes.oneOf(["web", "mobile"]),
};

export default Pagination;
 